import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import UserBadge from '../../../../Dashboard/DashboardComponents/UserBadge';
import Icon from '@amzn/meridian/icon';
import expandTokens from '@amzn/meridian-tokens/base/icon/expand';
import { RichCardProps } from '../../Types/types';
import { AssociateCardSummary } from '../../../../../Types';
import RichCardContent from './RichCardContent';
import { StateContext } from '../../../../../GlobalStateStore';
import Box from '@amzn/meridian/box';
import Tag from '@amzn/meridian/tag';
import Tooltip from '@amzn/meridian/tooltip';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';

const RichCard: React.FC<RichCardProps> = ({
  profile,
  associateCardSummary,
  filter,
  sideMenuSource,
  isBottomQuartile,
  BottomQuartileRoutingInfo,
  currentVertical,
  setHash,
  setOpenLinkHash,
  setExpanded,
  setFocusProfile,
}: RichCardProps): React.ReactElement => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername } = inspireOptions.data;

  const filteredRichCard: AssociateCardSummary[] = useMemo(() => {
    let filterKey = [
      'userID',
      'userAlias',
      'relationToUser',
      'bottomQuartileMetrics',
      filter,
    ];
    const filteredData = associateCardSummary.map(associateSummary => {
      // Ensure we always include the required isManager property
      const baseObject: AssociateCardSummary = {
        isManager: associateSummary.isManager,
      };

      // Add filtered properties
      Object.keys(associateSummary)
        .filter(key => filterKey.includes(key))
        .forEach(key => {
          (baseObject as any)[key] = associateSummary[key];
        });
      return baseObject;
    });
    return filteredData;
  }, [associateCardSummary, filter]);

  return (
    <Box type='outline' height='100%' width='100%'>
      <Column spacingInset='400' spacing='300' height='100%'>
        <Row alignmentHorizontal='justify'>
          <Row alignmentHorizontal='start'>
            <Row spacingInset='none' spacing='none'>
              <UserBadge username={profile.relatedUser.alias} thumbNail />
            </Row>
            <Row spacingInset='none' spacing='none'>
              <Column>
                <Heading level={2} type='h200'>
                  {profile.relatedUser.displayName}
                </Heading>
                {isBottomQuartile && !profile.isManager && (
                  <Row spacing='0 300' data-cy='tag-bottom-quartile-row'>
                    <Tag type='warning'>
                      {intl.formatMessage({
                        id: 'cs_perfportal_bq',
                        defaultMessage: 'Bottom Quartile',
                      })}
                    </Tag>
                    <Tooltip
                      position='bottom'
                      title={intl.formatMessage({
                        id: 'cs_perfportal_bq_tooltip',
                        defaultMessage:
                          'Quartile status is calculated based on data from the previous month.',
                      })}
                    >
                      <Icon tokens={infoTokens} />
                    </Tooltip>
                  </Row>
                )}
              </Column>
            </Row>
          </Row>

          <Row alignmentHorizontal='end'>
            <Button
              type='icon'
              onClick={() => {
                setExpanded(true);
                setHash(`${sideMenuSource}_${profile.relatedUser.alias}`);
                setOpenLinkHash(`${sideMenuSource}`);
                setFocusProfile(profile);
              }}
              data-csa-c-type='button'
              data-csa-c-slot-id='arc-mainview-expand-button'
              data-csa-c-tag1='Team'
              data-csa-c-tag2='Associates'
              data-csa-c-tag3='Card'
              data-csa-c-profile-id={requesterUserID}
              data-csa-c-username={requesterUsername}
              data-csa-c-is-tm={isTm}
              data-csa-c-content-id={`arc-mainview-expand-button-${profile.relatedUser.alias}`}
            >
              <Icon tokens={expandTokens} />
            </Button>
          </Row>
        </Row>
        {filteredRichCard &&
          filteredRichCard.map(
            currentCard =>
              currentCard.userAlias === profile.relatedUser.alias && (
                <RichCardContent
                  cardContent={currentCard}
                  sideMenuSource={sideMenuSource}
                  isBottomQuartile={isBottomQuartile && !currentCard.isManager}
                  BottomQuartileRoutingInfo={BottomQuartileRoutingInfo}
                  currentVertical={currentVertical}
                  key={currentCard.userAlias}
                />
              )
          )}
      </Column>
    </Box>
  );
};

export default RichCard;
